import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import MuiDrawer from '@mui/material/Drawer';
import ListItem from '@mui/material/ListItem';
import { CSSObject, styled, Theme } from '@mui/material/styles';
import * as React from 'react';
import { getUserRole, isAuthenticated } from './utils/auth-service';
import { BreakPoints } from './utils/enum';
import { Size, useWindowSize } from './utils/hooks';
import { IListItem, IPermissions } from './utils/interfaces';
import { menuItems } from './application/menu-items';
import DrawerItems from './drawer-items';
import { useAppSelector } from '../../redux/hooks';
import { Skeleton } from '@mui/material';
import _ from 'lodash';

const openedMixin = (theme: Theme): CSSObject => ({
  width: '300px',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: 'hidden'
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: 'hidden',
  width: '75px',
  [theme.breakpoints.up('xs')]: {
    width: '0px'
  },
  [theme.breakpoints.up('sm')]: {
    width: '75px'
  }
});
const SidebarTitle = styled('header')(({ theme }) => ({
  fontSize: '16px',
  fontFamily: theme.typography.fontFamily,
  fontWeight: theme.typography.fontWeightBold,
  color: theme.palette.primary.main,
  padding: 0,
  marginLeft: '28px',
  marginTop: '12px'
}));

const SidebarTitleMin = styled('header')(({ theme }) => ({
  fontSize: '5px',
  fontFamily: theme.typography.fontFamily,
  fontWeight: theme.typography.fontWeightBold,
  color: theme.palette.primary.main,
  padding: 0,
  marginLeft: 'auto',
  marginRight: 'auto',
  marginTop: '20px'
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open'
})(({ theme, open }) => ({
  position: 'relative',
  width: '240px',
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...((open ?? false) && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme)
  }),
  ...(!(open ?? false) && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme)
  })
}));

const StyledSkeleton = styled(Skeleton)<{ open: boolean }>(({ open }) => ({
  width: open ? '255px' : '90px',
  height: '956px'
}));

interface SidebarProps {
  open: boolean;
  setOpen: (value: boolean) => void;
}

export default function Sidebar({ open, setOpen }: SidebarProps): JSX.Element {
  const size: Size = useWindowSize();
  const { profileData } = useAppSelector((state) => state.profileData);
  const [userPermissions, setUserPermissions] = React.useState<IPermissions>();

  React.useEffect(() => {
    if (size.width !== undefined && size.width < BreakPoints.MD) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [size]);

  React.useEffect(() => {
    if (profileData) {
      if (profileData.permissions) {
        setUserPermissions(profileData.permissions);
      }
    }
  }, [profileData]);

  const role = getUserRole();

  const getMenuItems = (items: IListItem[]): {} => {
    const filteredMenuItems = items.filter(
      (item) => userPermissions?.[item.permissionGroup]?.read
    );
    return _.groupBy(filteredMenuItems, 'menuGroup');
  };

  const signInLoading = useAppSelector(
    (state) => state.auth.getSignInUrlLoading
  );
  const signOutLoading = useAppSelector(
    (state) => state.auth.getSignOutUrlLoading
  );

  return !signInLoading && !signOutLoading && isAuthenticated() && role ? (
    <>
      <Box
        sx={{
          display: 'flex',
          alignContent: 'center'
        }}
      >
        <CssBaseline />
        <Drawer
          variant="permanent"
          open={open}
          sx={{
            '& .MuiDrawer-paper': {
              backgroundColor: 'background.default',
              borderWidth: 0,
              position: 'relative'
            }
          }}
        >
          {open ? (
            <Box>
              <SidebarTitle>
                <img
                  src="/images/TereziTypeLogo.svg"
                  alt="TereziLogo"
                  width="auto"
                  height="30"
                />
              </SidebarTitle>
              <ListItem sx={{ height: '56px' }}></ListItem>
              <DrawerItems menuItem={getMenuItems(menuItems)} isOpen={open} />
            </Box>
          ) : (
            <Box>
              <Box
                sx={{
                  display: {
                    xs: 'none',
                    sm: 'flex'
                  }
                }}
              >
                <SidebarTitleMin>
                  <img
                    src="/images/TereziLogoT.svg"
                    alt="TereziLogoT"
                    width="auto"
                    height="30"
                  />
                </SidebarTitleMin>
              </Box>
              <ListItem sx={{ height: '56px' }}></ListItem>

              <Box
                sx={{
                  display: {
                    xs: 'none',
                    sm: 'flex'
                  }
                }}
              >
                <DrawerItems menuItem={getMenuItems(menuItems)} isOpen={open} />
              </Box>
            </Box>
          )}
        </Drawer>
      </Box>
    </>
  ) : (
    <StyledSkeleton variant="rectangular" open={open}></StyledSkeleton>
  );
}
